import { Reference } from '@apollo/client';
import { useContext, useEffect, useRef, useState } from 'react';

import Chat from '@components/messaging/Chat';
import styles from '@components/messaging/Messages.module.css';
import MessagesControls from '@components/messaging/MessagesControls';
import MessagesSmall from '@components/messaging/MessagesSmall';
import { openerContext } from '@context/OpenerStore';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import {
  ChatCreateDocument,
  ChatCreateMutation,
  ChatCreateMutationVariables,
  ChatFragment,
  ChatFragmentDoc,
  GetJourneyForMessagesQuery
} from '@typing/Generated';
import { OpenerType } from '@typing/State';
import { sortByTimestamp } from '@utils/modelUtils';

type Props = {
  journey: GetJourneyForMessagesQuery['journey'];
};

export type MessageSearch = {
  chatId: string | null;
  found: boolean | null;
  messageId: string | null;
  searchTerm: string | null;
};

export type ResourceData = {
  id: string;
  taskTitle: string | null;
  title: string;
};

export type WorksheetData = {
  id: string;
  title: string;
};

const Messages = ({ journey }: Props) => {
  const myClient = useMyClient();
  const [editingChat, setEditingChat] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  const [resourceData, setResourceData] = useState<ResourceData | undefined>();
  const [worksheetData, setWorksheetData] = useState<WorksheetData | undefined>();
  const { openerState, toggleOpener } = useContext(openerContext);
  const messagingOpenerState = openerState.find(opener => opener.type === OpenerType.MESSAGES);

  const messagingRecord = messagingOpenerState?.record;

  useEffect(() => {
    // When there is a messaging resource in the toggle state, take the data out of that resource and then clear the
    // state because we don't want the resource to persist as the messaging stuff gets closed and opened again.
    if (messagingRecord?.__typename === 'Resource') {
      setResourceData({
        id: messagingRecord.id,
        taskTitle: messagingRecord.task.title,
        title: messagingRecord.title
      });
    } else if (messagingRecord?.__typename === 'Worksheet') {
      setWorksheetData({
        id: messagingRecord.id,
        title: messagingRecord.title
      });
    }
    toggleOpener(OpenerType.MESSAGES, false, undefined);
  }, [messagingRecord, toggleOpener]);

  const [messageSearch, setMessageSearch] = useState<MessageSearch>({
    chatId: null,
    found: null,
    messageId: null,
    searchTerm: null
  });

  const [selectedChat, setSelectedChat] = useState<ChatFragment | undefined>(
    sortByTimestamp(journey.chats, chat => chat.lastMessage?.createdAt)?.[0]
  );

  const handleMessageSearchFind = (chatId: string, messageId: string, searchTerm: string) => {
    setSelectedChat(journey.chats.find(chat => chat.id === chatId));

    setMessageSearch({ chatId, found: false, messageId, searchTerm });
  };

  const [createChat] = useMutationWithValidation<ChatCreateMutation, ChatCreateMutationVariables>(
    ChatCreateDocument,
    'chat',
    {
      update: (store, { data }) => {
        if (data?.chatCreate?.chat) {
          store.modify({
            fields: {
              chats(cachedChatRefs: readonly Reference[]) {
                const newChatRef = store.writeFragment({
                  data: data.chatCreate?.chat,
                  fragment: ChatFragmentDoc,
                  fragmentName: 'Chat'
                });
                if (newChatRef && data.chatCreate?.chat) {
                  return [...cachedChatRefs, newChatRef];
                }
                return [...cachedChatRefs];
              }
            },
            id: store.identify({ ...journey })
          });
        }
      }
    }
  );

  const onChatClientsChange = (clientIds: string[]) => {
    const clientIdsWithMe = myClient ? [...clientIds, myClient.id] : clientIds;

    const existingChat = journey.chats.find(
      chat =>
        chat.clients.length === clientIdsWithMe.length &&
        chat.clients.map(client => client.id).every(clientId => clientIdsWithMe.includes(clientId))
    );

    if (existingChat) {
      setSelectedChat(existingChat);
      return;
    }

    if (clientIds.length > 0) {
      createChat({
        clientIds: clientIdsWithMe,
        journeyId: journey.id
      }).then(res => {
        if (res?.chatCreate?.chat) {
          setSelectedChat(res.chatCreate.chat);
        }
      });
    } else {
      setSelectedChat(undefined);
    }
  };

  return (
    <>
      <div className={styles.showMobile}>
        <MessagesSmall
          journey={journey}
          resourceData={resourceData}
          setResourceData={setResourceData}
          setWorksheetData={setWorksheetData}
          worksheetData={worksheetData}
        />
      </div>

      <div className={styles.messagesGrid + ' ' + styles.hideMobile}>
        <div>
          <MessagesControls
            handleMessageSearchFind={handleMessageSearchFind}
            journey={journey}
            messageSearch={messageSearch}
            selectedChat={selectedChat}
            setEditingChat={setEditingChat}
            setMessageSearch={setMessageSearch}
            setSelectedChat={setSelectedChat}
          />
        </div>

        {selectedChat ? (
          <div ref={chatRef}>
            <Chat
              chat={selectedChat}
              editingChat={editingChat}
              journey={journey}
              messageSearch={messageSearch}
              onChatClientsChange={onChatClientsChange}
              resourceData={resourceData}
              setMessageSearch={setMessageSearch}
              setResourceData={setResourceData}
              setWorksheetData={setWorksheetData}
              worksheetData={worksheetData}
            />
          </div>
        ) : (
          <div className={styles.emptyChat} />
        )}
      </div>
    </>
  );
};

export default Messages;
